import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { wizardError, wizardDone } from '../actions/wizard';

import {
  getConfiguration,
  requestReplacement,
  requestRetire,
  getReleaseVersion,
  requestEnableFAT,
  requestEnableOAT,
  requestSendOATResults,
  postCommand,
} from '../utils/api';

import {
  DEVICE_SET_INFO,
  DEVICE_SET_TARGET,
  DEVICE_SET_SECONDARY_TARGET,
  DEVICE_SET_RELEASE_VERSION,
} from '../constants/actionTypes';

export const setDeviceInfo = (id, device) => ({
  type: DEVICE_SET_INFO,
  id,
  device,
});

export const setReleaseVersion = releaseVersion => ({
  type: DEVICE_SET_RELEASE_VERSION,
  releaseVersion,
});

export const setTargetDevice = deviceId => ({
  type: DEVICE_SET_TARGET,
  target: deviceId,
});

export const setSecondaryTargetDevice = deviceId => ({
  type: DEVICE_SET_SECONDARY_TARGET,
  target: deviceId,
});

async function parseConfiguration(response) {
  const configuration = {};

  const configurationJson = await response.json();
  if (configurationJson.data && configurationJson.data.device) {
    const actualData = configurationJson.data.device;
    configuration.deviceCode = actualData.code;
    configuration.deviceName = actualData.name;
    configuration.deviceType = actualData.type;
    configuration.deviceOrganization = actualData.owners
      ? actualData.owners[0].id
      : null;
    configuration.deviceSubType = actualData.subType;
    configuration.lifeCycleStatus = actualData.lifeCycleStatus;
    configuration.osVersion = actualData.osVersion;

    if (actualData.metadata && actualData.metadata.raw) {
      const metadata = actualData.metadata.raw;
      configuration.deviceTags = metadata.tags;
    }
  } else {
    return null;
  }

  return configuration;
}

export const fetchReleaseVersion = () => async dispatch => {
  dispatch(showLoading());
  try {
    const response = await getReleaseVersion();
    dispatch(hideLoading());

    if (response.status === 200) {
      const versions = await response.json();
      if (Array.isArray(versions) && versions.length !== 0) {
        dispatch(setReleaseVersion(versions));
      } else {
        dispatch(wizardError({ title: 'errors.release_version_not_found' }));
      }
    } else if (response.status === 403) {
      dispatch(
        wizardError({
          title: 'errors.configure_forbidden',
          hint: 'errors.contact_administrators_hint',
        })
      );
    } else {
      throw new Error(`Unexpected response status: ${response.status}`);
    }
  } catch (error) {
    dispatch(hideLoading());
    console.error(error);
    dispatch(
      wizardError({
        title: error.message,
      })
    );
  }
};

export const retrieveConfiguration = deviceId => async dispatch => {
  dispatch(showLoading());
  try {
    const response = await getConfiguration(deviceId);
    dispatch(hideLoading());

    if (response.status === 200) {
      const config = await parseConfiguration(response);
      if (config === null) {
        dispatch(wizardError({ title: 'errors.device_not_found' }));
      } else {
        dispatch(setDeviceInfo(deviceId, config));
      }
    } else if (response.status === 403) {
      dispatch(
        wizardError({
          title: 'errors.configure_forbidden',
          hint: 'errors.contact_administrators_hint',
        })
      );
    } else {
      throw new Error(`Unexpected response status: ${response.status}`);
    }
  } catch (error) {
    dispatch(hideLoading());
    throw error;
  }
};

export const fetchAndSelectDevice = deviceId => async dispatch => {
  await dispatch(retrieveConfiguration(deviceId));
  await dispatch(fetchReleaseVersion());

  await dispatch(setTargetDevice(deviceId));
};

export const fetchAndSelectSecondary = deviceId => async dispatch => {
  await dispatch(retrieveConfiguration(deviceId));
  await dispatch(setSecondaryTargetDevice(deviceId));
  await dispatch(fetchReleaseVersion());
};

export const replaceDevice = (targetId, replacementId) => async dispatch => {
  dispatch(showLoading);
  try {
    const response = await requestReplacement(targetId, replacementId);
    dispatch(hideLoading);

    if (response.status === 201) {
      dispatch(wizardDone('messages.replacement_success'));
    } else if (response.status === 403) {
      dispatch(
        wizardError({
          title: 'errors.configure_forbidden',
          hint: 'errors.contact_administrators_hint',
        })
      );
    } else {
      dispatch(
        wizardError({
          title: 'errors.configure_failed_general',
        })
      );
    }
  } catch (error) {
    dispatch(hideLoading);
    console.error(error);
    dispatch(
      wizardError({
        title: error.message,
      })
    );
  }
};

export const retireDevice = (
  targetId,
  retiredReasonId = 1
) => async dispatch => {
  dispatch(showLoading);
  try {
    const response = await requestRetire(targetId, retiredReasonId);
    dispatch(hideLoading);

    if (response.status === 201) {
      dispatch(wizardDone('messages.retire_success'));
    } else if (response.status === 403) {
      dispatch(
        wizardError({
          title: 'errors.configure_forbidden',
          hint: 'errors.contact_administrators_hint',
        })
      );
    } else {
      dispatch(
        wizardError({
          title: 'errors.configure_failed_general',
        })
      );
    }
  } catch (error) {
    dispatch(hideLoading);
    console.error(error);
    dispatch(
      wizardError({
        title: error.message,
      })
    );
  }
};

export const enableFAT = (deviceCode, organization) => async dispatch => {
  dispatch(showLoading);
  try {
    const response = await requestEnableFAT(deviceCode, organization);
    dispatch(hideLoading);

    if (response.status === 200) {
      dispatch(wizardDone('messages.enableFAT_success'));
    } else if (response.status === 403) {
      dispatch(
        wizardError({
          title: 'errors.FAT_forbidden',
          hint: 'errors.contact_administrators_hint',
        })
      );
    } else {
      dispatch(
        wizardError({
          title: 'errors.configure_failed_general',
        })
      );
    }
  } catch (error) {
    dispatch(hideLoading);
    console.error(error);
    dispatch(
      wizardError({
        title: error.message,
      })
    );
  }
};

export const enableOAT = (deviceCode, organization) => async dispatch => {
  dispatch(showLoading);
  try {
    const response = await requestEnableOAT(deviceCode, organization);
    dispatch(hideLoading);

    if (response.status === 200) {
      dispatch(wizardDone('messages.enableOAT_success'));
    } else if (response.status === 403) {
      dispatch(
        wizardError({
          title: 'errors.OAT_forbidden',
          hint: 'errors.contact_administrators_hint',
        })
      );
    } else {
      dispatch(
        wizardError({
          title: 'errors.configure_failed_general',
        })
      );
    }
  } catch (error) {
    dispatch(hideLoading);
    console.error(error);
    dispatch(
      wizardError({
        title: error.message,
      })
    );
  }
};

export const sendOATResults = oatResults => async dispatch => {
  dispatch(showLoading);
  try {
    const response = await requestSendOATResults(oatResults);
    dispatch(hideLoading);

    if (response.status === 200) {
      oatResults.isAccepted
        ? dispatch(wizardDone('messages.accept_OAT_results'))
        : dispatch(
            wizardError({
              title: 'messages.reject_OAT_results',
              hint: 'errors.please_contact_support',
              showDeviceInput: false,
            })
          );
    } else if (response.status === 403) {
      dispatch(
        wizardError({
          title: 'errors.OAT_results_forbidden',
          hint: 'errors.contact_administrators_hint',
          showDeviceInput: false,
        })
      );
    } else {
      dispatch(
        wizardError({
          title: 'errors.configure_failed_general',
          showDeviceInput: false,
        })
      );
    }
  } catch (error) {
    dispatch(hideLoading);
    console.error(error);
    dispatch(
      wizardError({
        title: error.message,
        showDeviceInput: false,
      })
    );
  }
};

export const sendShelfIdentificationCommand = (
  deviceCode,
  command
) => async dispatch => {
  dispatch(showLoading);
  try {
    const params = { readerType: 'scale1' };
    const response = await postCommand(deviceCode, command, params);
    dispatch(hideLoading);

    if (response.status === 200 || response.status === 201) {
      if (command === 'startIdentifyShelves') {
        // nothing to do here ...
      } else {
        dispatch(wizardDone('messages.stop_shelf_identification_success'));
      }
    } else if (response.status === 403) {
      dispatch(
        wizardError({
          title: 'errors.shelf_identification_forbidden',
          hint: 'errors.contact_administrators_hint',
        })
      );
    } else {
      dispatch(
        wizardError({
          title: 'errors.configure_failed_general',
        })
      );
    }
  } catch (error) {
    dispatch(hideLoading);
    console.error(error);
    dispatch(
      wizardError({
        title: error.message,
      })
    );
  }
};

export const sendDiscoveryCommand = (deviceCode, command) => async dispatch => {
  dispatch(showLoading);
  try {
    const params = { readerType: 'scale1' };
    const response = await postCommand(deviceCode, command, params);
    dispatch(hideLoading);

    if (response.status === 200 || response.status === 201) {
      if (command === 'startScaleSlotDiscoveryMode') {
        // nothing to do here ...
      } else {
        dispatch(wizardDone('messages.stop_discovery_command_success'));
      }
    } else if (response.status === 403) {
      dispatch(
        wizardError({
          title: 'errors.discovery_mode_forbidden',
          hint: 'errors.contact_administrators_hint',
        })
      );
    } else {
      dispatch(
        wizardError({
          title: 'errors.configure_failed_general',
        })
      );
    }
  } catch (error) {
    dispatch(hideLoading);
    console.error(error);
    dispatch(
      wizardError({
        title: error.message,
      })
    );
  }
};
